import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import queryString from 'query-string';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { isIOS } from 'react-device-detect';

import useFetchPlayerConfigData from './components/Player/hooks/useFetchPlayerConfigurationData';
import useFetchInteractProjectData from './components/Player/hooks/useFetchInteractProjectData';
import useProcessMediaData from './components/Player/hooks/useProcessMediaData';
import ShareMediaDialog from './components/Controls/ShareMediaDialog';
import { QualitySettingMenu } from './components/Controls/SettingsMenu/QualitySettingMenu';
import { LanguageSettingMenu } from './components/Controls/SettingsMenu/LanguageSettingMenu';
import Poster from './components/Poster/Poster';
import CountDown from './components/CountDown/CountDown';
import TransitionLayer from './components/Interact/InteractTransition/TransitionLayer';
import Player from './components/Player/Player';
import InteractOverlay from './components/Interact/InteractOverlay/InteractOverlay';
import Controls from './components/Controls/Controls';
import ShoppingBasket from './components/ShoppingBasket/ShoppingBasket';
import ChatOverlay from './components/ChatOverlay/ChatOverlay';
import Playlist from './components/Playlist/Playlist';
import PasswordRestrictionContainer from './components/PasswordRestrictionContainer/PasswordRestrictionContainer';
import { setCurrentSceneWidgets } from './actions/playerActions';
import {
	setCurrentMediaId,
	setEmbedShareSettings,
	setMediasData,
	setOpenSettingMenu,
	setSceneConfig,
	setScreenType,
	setMinimizedPlayerState,
} from './actions/configurationActions';
import useGoogleAnalytic from './hooks/useGoogleAnalytic';
import {
	EDITOR_PLAYER_ID,
	getSecondsFromTimelineTime,
	getTimelineTimeFromTimeInput,
	isUsedOnMobile as isUsedOnMobileFunc,
	joinClassNames,
	SCREEN_TYPE,
	STORY_CONFIG_ID,
} from './util/util';
import { getMediaRestrictToken } from './util/media-data-util';
import { MessageHelper } from './util/message-helper';

import './App.scss';
import { TranscriptionContainer } from "./components/TranscriptionModule/TranscriptionContainer";
import { useFetchTranscriptData } from "./components/Controls/SettingsMenu/useFetchTranscriptData";
import SubtitleSettingMenu from './components/Controls/SettingsMenu/SubtitleSettingMenu';
import 'swiper/css';

const App = () => {
	const dispatch = useDispatch();

	const countdownRef = useRef<any | null>(null);
	const playerHoverTimeoutId = useRef<any | null>(null);
	const resetPlayerHoverRef = useRef<boolean | null>(null);
	const androidInteractOverlayPositionUpdated = useRef(false);
	const languageSettingMenuRef = useRef<any | null>(null);

	const [loading, setLoading] = useState(true);
	const [sideWindowOpen, setSideWindowOpen] = useState(false);
	const [playerHover, setPlayerHover] = useState(false);
	const [tokens, setTokens] = useState<string[] | null>(null); // Initial tokens state has to be null
	const [hideRightControls, setHideRightControls] = useState(true);
	const [analyticsCloseEvent, setAnalyticsCloseEvent] = useState(false);

	const { adsPlaying, currentPosition, showShoppingCart, showChatWindow, isPlaying, duration } = useSelector((state) => (state as any).playerReducer);
	const {
		restrictedMedias,
		relatedMediasOfScene,
		screenType,
		mediaType,
		scenario,
		sceneConfig,
		currentMediaId,
		playlistId,
		mediaName,
		accountId,
		isMediaLive,
		currentConfigId,
		mediasData,
		restrictedToken,
		viewerEngagementModuleActivated,
		openSettingMenu,
		showTranscriptionsModule,
	} = useSelector((state) => (state as any).configurationReducer);
	const { unlockedMedias } = useSelector((state) => (state as any).restrictionReducer);

	const queryParams = useMemo(() => queryString.parse(window.location.search), []);
	const isInlinePlayer = useMemo(() => queryParams?.inline === 'on', [queryParams]);
	const isLoadedByLoader = useMemo(() => queryParams?.useLoader === 'true', [queryParams]);
	const fromSceneEditor = useMemo(() => queryParams.fromSceneEditor === 'true', [queryParams]);
	const isUsedOnMobile = useMemo(() => isUsedOnMobileFunc(queryParams), [queryParams]);
	const screenBreakpointClass = useMemo(() => {
		if (isUsedOnMobile) {
			return '';
		}
		switch (screenType) {
			case SCREEN_TYPE.mobile:
				return 'qplayer-device-mobile';
			case SCREEN_TYPE.small:
				return 'qplayer-small-screen';
			case SCREEN_TYPE.medium:
				return 'qplayer-medium-screen';
			case SCREEN_TYPE.big:
				return 'qplayer-big-screen';
			case SCREEN_TYPE.huge:
				return 'qplayer-huge-screen';
			default:
				return '';
		}
	}, [isUsedOnMobile, screenType]);

	const getRestrictToken = useCallback((mediaId?: string, isScenario = false) => getMediaRestrictToken({ mediaId, isScenario, tokens: tokens ?? [], restrictedToken, scenario, unlockedMedias }), [restrictedToken, scenario, tokens, unlockedMedias]);

	const setLoadingCallback = useCallback((value: boolean) => {
		setLoading(value);
	}, []);
	const updateAndroidInteractOverlayPositionCallback = useCallback(() => (androidInteractOverlayPositionUpdated.current = false), []);
	const getScenarioTokenCallback = useCallback(() => getRestrictToken(undefined, true), [getRestrictToken]);

	const { playerMobileConfig, playerDesktopConfig } = useFetchPlayerConfigData({
		playerConfigId: queryParams?.configId as string,
		accountId: queryParams?.accountId as string,
		fromSceneEditor,
		queryParams,
		isMediaLive,
		setLoading: setLoadingCallback,
	});
	useProcessMediaData({
		playlistId: queryParams?.playlistId as string,
		mediaId: queryParams?.mediaId as string,
		accountId: queryParams?.accountId as string,
	});
	useFetchInteractProjectData({
		tokens,
		scenarioId: queryParams?.scenarioId as string,
		accountId: queryParams?.accountId as string,
		isUsedOnMobile,
		fromSceneEditor,
		setLoading: setLoadingCallback,
		updateAndroidInteractOverlayPosition: updateAndroidInteractOverlayPositionCallback,
		getRestrictToken: getScenarioTokenCallback,
		queryParams
	});
	useFetchTranscriptData({
		showTranscriptSettings: (isUsedOnMobile ? playerMobileConfig : playerDesktopConfig)?.bottomControls?.showTranscriptSettings,
	});
	useGoogleAnalytic({
		scenario,
		playerDesktopConfig,
		playerMobileConfig,
		queryParams,
		currentMediaId,
		sceneConfig,
		isPlaying,
		currentPosition,
		mediaName,
		accountId,
	});

	const storyPlaylistInitPosition = queryParams?.initPosition ? +queryParams?.initPosition : 0;
	const handleHiddenControlBar = () => {
		if (playerHoverTimeoutId.current) {
			clearTimeout(playerHoverTimeoutId.current);
		}
		if (isPlaying === false) {
			return;
		}

		playerHoverTimeoutId.current = setTimeout(() => {
			if (resetPlayerHoverRef.current === true || !resetPlayerHoverRef) {
				return;
			}
			const menus = document.querySelectorAll('.qplayer__language-container,.qplayer__setting-menu,.qplayer__subtitle-setting,.qplayer__sharing-dialog');
			if (menus.length > 0 && window.innerWidth > 600) {
				return;
			}

			setPlayerHover(false);
		}, 3000);
	};

	const handlePlayerHover = useCallback( () => {
		setPlayerHover(true);
		handleHiddenControlBar();
	}, [isPlaying]);

	const renderSceneEditorPlayer = useCallback(() => {
		return currentMediaId && currentMediaId !== '' && accountId !== '' && playerDesktopConfig !== null ? (
			<Player
				key={EDITOR_PLAYER_ID + currentMediaId}
				accountId={accountId}
				isVisible
				playerMedia={currentMediaId}
				settings={playerDesktopConfig}
				queryParams={{}}
				layout={'desktop'}
				playerId={EDITOR_PLAYER_ID}
			/>
		) : null;
	}, [accountId, currentMediaId, playerDesktopConfig]);

	const renderPlayers = useCallback(
		(showPasswordScreen = false) => {
			const config = isUsedOnMobile ? playerMobileConfig : playerDesktopConfig;
			if (!accountId || (!currentMediaId && !playlistId) || !config) {
				return null;
			}

			const isStoryPlaylist = playlistId && queryParams?.configId === STORY_CONFIG_ID;
			const showControls = queryParams?.noControls !== 'true' && !showPasswordScreen;

			return (
				<>
					<div className="playerWindow" style={isUsedOnMobile ? { width: '100%' } : { width: sideWindowOpen ? '75%' : '100%' }}>
						{mediaType === 'scenario' &&
							mediasData.map((media: any) => {
								const mediaId = media.id;
								const endOfScene = media.endOfScene;
								const relatedMediaData = relatedMediasOfScene[sceneConfig.currentScene] ?? [];
								const isNeededToInit = relatedMediaData.find((id: string) => id === mediaId);

								if (isNeededToInit) {
									return (
										<Player
											key={mediaId}
											accountId={accountId}
											isVisible={currentMediaId === mediaId}
											playerMedia={mediaId}
											settings={config}
											queryParams={queryParams}
											layout={isUsedOnMobile ? 'mobile' : 'desktop'}
											isMaintainProportions={isUsedOnMobile ? config?.playbackOptions?.maintainProportions : false}
											playerId={`player-${mediaId}`}
											endOfScene={endOfScene}
											token={getRestrictToken(mediaId)}
											handlePlayerHover={handlePlayerHover}
										/>
									);
								}
							})}

						{mediaType !== 'scenario' && !isStoryPlaylist && (
							<Player
								accountId={accountId}
								isVisible={true}
								playerMedia={playlistId || currentMediaId}
								settings={config}
								queryParams={queryParams}
								layout={isUsedOnMobile ? 'mobile' : 'desktop'}
								isMaintainProportions={isUsedOnMobile ? config?.playbackOptions?.maintainProportions : false}
								playerId={`player-${playlistId || currentMediaId}`}
								token={getRestrictToken(currentMediaId ?? playlistId)}
								handlePlayerHover={handlePlayerHover}
							/>
						)}

						{isStoryPlaylist && mediasData?.length > 0 && (
							<Swiper
								cssMode
								navigation
								simulateTouch
								slidesPerView={'auto'}
								spaceBetween={10}
								modules={[Navigation]}
								onInit={() => {
									dispatch(setCurrentMediaId(mediasData[storyPlaylistInitPosition].id));
									setTimeout(() => {
										(window as any).GoBrain.widgets(`player-${mediasData[storyPlaylistInitPosition].id}`)?.play?.();
									}, 1000);
								}}
								initialSlide={storyPlaylistInitPosition}
								onActiveIndexChange={(swiper: any) => {
									(window as any).GoBrain.widgets(`player-${currentMediaId}`)?.pause?.();
									const nextActivePlayerId = mediasData[swiper.activeIndex].id;
									(window as any).GoBrain.widgets(`player-${nextActivePlayerId}`)?.play?.();
									dispatch(setCurrentMediaId(nextActivePlayerId));
								}}
							>
								{mediasData.map((media: any, _index: number) => {
									const mediaId = media.id;
									const thumbnail = media.images?.[0]?.url;
									return (
										<SwiperSlide
											key={mediaId}
											style={{
												scrollSnapStop: 'always',
											}}
										>
											{({ isActive }) => {
												return (
													<div
														style={{
															width: '100vw',
															height: '100vh',
															overflow: 'hidden',
															position: 'relative',
															background: `url(${thumbnail}) center / cover no-repeat`,
														}}
													>
														<Player
															accountId={accountId}
															isVisible={isActive}
															playerMedia={mediaId}
															settings={config}
															queryParams={queryParams}
															layout={isUsedOnMobile ? 'mobile' : 'desktop'}
															isMaintainProportions={
																isUsedOnMobile ? config?.playbackOptions?.maintainProportions : false
															}
															playerId={`player-${mediaId}`}
															token={getRestrictToken(currentMediaId ?? playlistId)}
															handlePlayerHover={handlePlayerHover}
														/>
													</div>
												);
											}}
										</SwiperSlide>
									);
								})}
							</Swiper>
						)}

						{showControls && (
							<>
								{isPlaying !== undefined && (
									<Controls
										isMobile={isUsedOnMobile}
										playerHover={playerHover}
										settings={config}
										queryParams={queryParams}
										isInlinePlayer={isInlinePlayer}
										isMaintainProportions={isUsedOnMobile ? config?.playbackOptions?.maintainProportions : false}
										onMouseEnter={() => {
											resetPlayerHoverRef.current = true;
											handlePlayerHover();
										}}
										onMouseLeave={() => {
											resetPlayerHoverRef.current = false;
											handlePlayerHover();
										}}
									/>
								)}
								<InteractOverlay
									isMobile={isUsedOnMobile}
									settings={config}
									sideWindowOpen={isUsedOnMobile ? false : sideWindowOpen}
									isInlinePlayer={isInlinePlayer}
								/>
							</>
						)}
					</div>

					{isPlaying !== undefined && showControls && <ShoppingBasket settings={config} />}
					{showControls && (
						<ChatOverlay
							showChatWindow={showChatWindow}
							settings={config}
							hideRightControls={hideRightControls}
							getRestrictToken={getRestrictToken}
							playerHover={playerHover}
						/>
					)}
					{showControls && showTranscriptionsModule && (
						<TranscriptionContainer settings={config} queryParams={queryParams} isMobile={isUsedOnMobile} />
					)}

					{playlistId !== '' && showControls && !adsPlaying && (
						<Playlist
							playlistId={playlistId}
							settings={config}
							playerHover={playerHover}
							queryParams={queryParams}
						/>
					)}
				</>
			);
		},
		[
			accountId,
			currentMediaId,
			playerDesktopConfig,
			getRestrictToken,
			handlePlayerHover,
			hideRightControls,
			isInlinePlayer,
			isPlaying,
			isUsedOnMobile,
			mediaType,
			mediasData,
			playerMobileConfig,
			playerHover,
			playlistId,
			queryParams,
			relatedMediasOfScene,
			sceneConfig.currentScene,
			showChatWindow,
			sideWindowOpen,
			showTranscriptionsModule,
			dispatch,
			adsPlaying
		]
	);

	const closeSettingsMenus = useCallback(() => {
		dispatch(setOpenSettingMenu(null));
	}, [dispatch]);

	const executeDataFromEditor = (editorData: any) => {
		const editingScene = editorData.scene;
		const activeLayout = editorData.activeLayout;

		if (!editingScene) {
			return;
		}

		let mediasToLoad: any[] = [];
		// GET ALL MEDIA IN SCENE
		let layout = editingScene?.layouts?.find((l: any) => l.type === activeLayout);
		layout = layout ?? editingScene?.layouts?.[0];
		const boxes = layout?.boxes ?? [];
		let mediaData = null;
		let start = 0;
		let videoLength = 0;

		const endOfScene = (editingScene.events ?? [])
			.find((e: any) => e.type === 'endOfScene')
			?.actions?.find((action: any) => ['goToScene', 'loopCurrentScene', 'goToHome'].includes(action.type));

		if (boxes.length > 0) {
			let widgets: any[] = [];
			boxes.forEach((box: any) => {
				box.widgets?.forEach((w: any) => widgets.push(w));
			});
			editingScene.widgetTemplates.forEach((tmp: any) => {
				const currentBoxWidget = widgets.find((w) => w.widgetTemplateId === tmp.id);
				if (!currentBoxWidget) {
					return;
				}
				if (tmp.type === 'video' && !mediasToLoad.find((m) => m.id === tmp.settings?.mediaId)) {
					start = currentBoxWidget.start && moment(getTimelineTimeFromTimeInput(currentBoxWidget.start));
					const end = currentBoxWidget.end && moment(getTimelineTimeFromTimeInput(currentBoxWidget.end));
					videoLength = getSecondsFromTimelineTime(end) - getSecondsFromTimelineTime(start) + 1;
					mediaData = {
						id: tmp.settings?.mediaId,
						start: getSecondsFromTimelineTime(start),
						end: getSecondsFromTimelineTime(end),
						videoLength: videoLength,
						isMediaLive: false,
						mediaName: tmp.name,
						sceneId: editingScene.id,
					};
					mediasToLoad.push({ id: tmp.settings?.mediaId, sceneId: editingScene.id, endOfScene: endOfScene });
				}
			});
		}

		dispatch(setCurrentMediaId(mediasToLoad[0].id));
		mediasToLoad = Array.from(new Set(mediasToLoad));
		dispatch(setMediasData([mediaData]));
		dispatch(setCurrentSceneWidgets(null));
		dispatch(
			setSceneConfig({
				currentScene: editingScene.id,
				nextScene: editingScene.id,
			})
		);
	};

	const handleKeyup = (event: KeyboardEvent) => {
		if (event.key === 'Tab') {
			resetPlayerHoverRef.current = true;
			setPlayerHover(true);

			if (openSettingMenu) {
				const isCloseableByTabMenu =
					!!(event.target as any).classList.contains('closeable-by-tab') ||
					!!(event.target as any)?.children?.[0].classList.contains('closeable-by-tab');

				if (isCloseableByTabMenu) {
					dispatch(setOpenSettingMenu(undefined));
				} else if ((event.target as any).tabIndex === -1) {
					// Avoid redundant tab
					const elements = (event.target as any).querySelectorAll('li');

					if (elements.length > 0) {
						event.shiftKey ? elements[elements.length - 1].focus() : elements[0].focus();
					}
				}
			}
			handleHiddenControlBar();
		}

		if (event.key === 'Enter') {
			resetPlayerHoverRef.current = true;
			setPlayerHover(true);
			handleHiddenControlBar();
			return;
		}

		resetPlayerHoverRef.current = false;
		handleHiddenControlBar();
	};

	const handleOnMessage = useCallback(
		(event) => {
			if (event.data?.type === 'qplayer_tokens' && event.data?.tokens) {
				setTokens(event.data?.tokens ?? []);
			}
			if (event.data?.type === 'qplayer-embed-origin') {
				dispatch(setEmbedShareSettings(event.data));
			}

			if (event.data?.type === 'updateScene') {
				executeDataFromEditor(event.data);
			}

			const widgets = (window as any).GoBrain.widgets()[`player-${playlistId || currentMediaId}`];

			// Control the player from the loader
			if (event.data && event.data.identifier === MessageHelper.MESSAGE_BASE_IDENTIFIERS.loaderIdentifier) {
				if (event.data.command === 'play') {
					if (
						(document.body.querySelector('div[data-internal-gobrain-box-configuration-modulename="Covers"]') as any)?.style.display !==
						'block'
					) {
						widgets?.play?.();
					}
				} else if (event.data.command === 'pause' || event.data.command?.data === 'pause') {
					widgets?.pause?.();
				} else if (event.data.command === 'play-minimized') {
					widgets?.play?.();
				} else if (event.data.command === 'maximize') {
					setHideRightControls(false);
				} else if (event.data.command === 'close-modal') {
					widgets?.pause();
					setHideRightControls(false);
					setAnalyticsCloseEvent(true);
				} else if (event.data.command === 'minimize') {
					setHideRightControls(true);
				}

				if (event.data?.command === MessageHelper.MESSAGE_TYPES.minimizePlayer) {
					widgets?.pause?.();
					dispatch(setMinimizedPlayerState(true));
				}
				if (event.data?.command === MessageHelper.MESSAGE_TYPES.maximizePlayer) {
					typeof event?.data?.position === 'number' && widgets?.position?.(event.data.position);
					typeof event?.data?.volume === 'number' && widgets?.volume?.(event.data.volume);
					widgets?.play?.();
					dispatch(setMinimizedPlayerState(false));
				}
			}

			// Alow customers to control the player from the parent window
			if (event.data && event.data.identifier === MessageHelper.MESSAGE_BASE_IDENTIFIERS.embeddedIdentifier) {
				if (event.data.command === MessageHelper.MESSAGE_TYPES.play) {
					widgets?.play?.();
				}
				if (event.data.command === MessageHelper.MESSAGE_TYPES.pause) {
					widgets?.pause?.();
				}
				if (event.data.command === MessageHelper.MESSAGE_TYPES.position) {
					widgets?.position?.(event.data?.metadata?.position);
				}
				if (event.data.command === MessageHelper.MESSAGE_TYPES.volume) {
					widgets?.volume?.(event.data?.metadata?.volume);
				}
			}

			if (event.data && (event.data.command?.data ?? '').includes('play')) {
				setHideRightControls(false);
			}
		},
		[currentMediaId, dispatch, playlistId]
	);

	const changeScreenSize = useCallback(() => {
		const width = window.innerWidth;
		countdownRef.current?.changeLimitedTitleLines(width < 700 ? 3 : 4);

		if (isUsedOnMobile) {
			return;
		}

		let newScreenType = SCREEN_TYPE.huge;
		if (width <= 430) {
			newScreenType = SCREEN_TYPE.mobile;
		} else if (width <= 600) {
			newScreenType = SCREEN_TYPE.small;
		} else if (width <= 768) {
			newScreenType = SCREEN_TYPE.medium;
		} else if (width <= 1280) {
			newScreenType = SCREEN_TYPE.big;
		}
		if (screenType !== newScreenType) {
			dispatch(setScreenType(newScreenType));
		}
	}, [dispatch, isUsedOnMobile, screenType]);

	useEffect(() => {
		changeScreenSize();

		window.addEventListener('message', handleOnMessage);
		window.addEventListener('resize', changeScreenSize);

		return () => {
			window.removeEventListener('message', handleOnMessage);
			window.removeEventListener('resize', changeScreenSize);
		};
	}, [changeScreenSize, handleOnMessage]);

	// need to move this event out cause handleKeyup need to get the openSettingMenu value
	useEffect(() => {
		window.addEventListener('keyup', handleKeyup);
		return () => {
			window.removeEventListener('keyup', handleKeyup);
		};
	}, [openSettingMenu]);

	useEffect(() => {
		setSideWindowOpen(
			isUsedOnMobile ? false : showShoppingCart || (viewerEngagementModuleActivated && showChatWindow) || showTranscriptionsModule
		);
	}, [showShoppingCart, showChatWindow, viewerEngagementModuleActivated, isUsedOnMobile, showTranscriptionsModule]);

	useEffect(() => {
		if (isPlaying) {
			const currentActive = document.activeElement;
			if (!currentActive || currentActive.closest('.controlsOverlay') || currentActive.closest('.chat-container')) {
				return;
			}

			const controlsOverlay = document.getElementsByClassName('controlsOverlay')[0] as any;
			if (!controlsOverlay) {
				return;
			}
			if (!isInlinePlayer) {
				controlsOverlay.focus();
			}

			handlePlayerHover();
		} else {
			setPlayerHover(true);
		}

		if (isPlaying === undefined) {
			setSideWindowOpen(false);
		}
	}, [isPlaying, isInlinePlayer, handlePlayerHover]);

	useEffect(() => {
		if (analyticsCloseEvent) {
			const currentMedia = mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId);
			window.parent.postMessage(
				{
					origin: 'Qbrick.Interact.Widget.Element',
					identifier: 'qbrick-player-analytics-message',
					command: {
						type: 'AnalyticsEvent',
						action: 'click',
						data: 'closePlayer',
						mediaName: mediaName,
						mediaId: currentMediaId,
						accountId: accountId,
						videoLength: currentMedia?.duration ?? duration,
						isMediaLive: isMediaLive,
						currentPosition: isMediaLive ? Date.now() : currentPosition,
					},
				},
				'*'
			);
			setAnalyticsCloseEvent(false);
		}
	}, [
		currentMediaId,
		accountId,
		currentConfigId,
		playerDesktopConfig,
		playerMobileConfig,
		analyticsCloseEvent,
		mediaName,
		isMediaLive,
		currentPosition,
		duration,
		mediasData,
	]);

	const checkSubtitleBottomMargin = useCallback(() => {
		const bottomControlBar = document.querySelector('div.bottomControlbarHover > .bottomControlbar');
		const hoverMarginBottom = bottomControlBar?.getBoundingClientRect()?.height;
		const subtitleModule = document.querySelector('div[data-internal-gobrain-box-configuration-modulename=Subtitles]') as any;

		if (subtitleModule) {
			const marginBottom = playerHover && hoverMarginBottom === undefined ? 60 : hoverMarginBottom ?? 0;
			subtitleModule.style.marginBottom = marginBottom + 'px';
		}
	}, [playerHover]);
	useEffect(checkSubtitleBottomMargin, [checkSubtitleBottomMargin]);

	const shouldShowPasswordScreen = restrictedMedias.includes(currentMediaId) || restrictedMedias.includes("scenario");
	const mobileClassName = isUsedOnMobile || queryParams?.fromQplayerWidget === "true" ? 'qplayer-app--mobile' : '';

	return (
		<div
			id="main-container"
			className={joinClassNames(
				'qplayer-app',
				isLoadedByLoader ? 'qplayer-app--loader' : '',
				adsPlaying ? 'qplayer-app--ads-playing' : '',
				mobileClassName,
				screenBreakpointClass
			)}
			onMouseEnter={handlePlayerHover}
			onMouseMove={handlePlayerHover}
			onMouseLeave={() => {
				resetPlayerHoverRef.current = false;
				if (playerHoverTimeoutId.current) {
					clearTimeout(playerHoverTimeoutId.current);
				}
				if (isPlaying === false) {
					setPlayerHover(true);
					return;
				}

				setPlayerHover(false);
			}}
			onMouseDown={closeSettingsMenus}
			onTouchMove={handlePlayerHover}
			onTouchEnd={(event) => {
				if (isIOS && (event.target as HTMLElement).className.includes('mute-button')) {
					const currentPlayerId = `player-${playlistId || currentMediaId}`;
					(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(0.5);
				}

				if (isPlaying === false) {
					setPlayerHover(true);
					return;
				}
				resetPlayerHoverRef.current = false;
				handlePlayerHover();
			}}
			tabIndex={-1}
		>
			{!loading && (
				<>
					{shouldShowPasswordScreen && (
						<PasswordRestrictionContainer
							isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
							playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
						/>
					)}
					{!shouldShowPasswordScreen && !fromSceneEditor && <>
						<QualitySettingMenu
							className={screenBreakpointClass}
							sideWindowOpen={isUsedOnMobile ? false : sideWindowOpen}
							playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
							isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
						/>
						<LanguageSettingMenu
							className={screenBreakpointClass}
							sideWindowOpen={isUsedOnMobile ? false : sideWindowOpen}
							playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
							isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
							queryParams={queryParams}
							onLanguageChange={(type, _code) => type === "subtitle" && checkSubtitleBottomMargin() }
						/>
						<SubtitleSettingMenu languageSettingMenuRef={languageSettingMenuRef} sideWindowOpen={sideWindowOpen} />
					</>}
					{!fromSceneEditor && renderPlayers(shouldShowPasswordScreen)}
					{!shouldShowPasswordScreen && !fromSceneEditor && <>
						<ShareMediaDialog
							sideWindowOpen={isUsedOnMobile ? false : sideWindowOpen}
							playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
							isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
						/>
						<Poster
							playerId={`player-${queryParams.playlistId ? playlistId : currentMediaId}`}
							queryParams={queryParams}
							settings={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
							isEditorPlayer={fromSceneEditor}
							isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
							playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
						/>
						<CountDown
							isMobile={isUsedOnMobile || screenType === SCREEN_TYPE.small || screenType === SCREEN_TYPE.mobile}
							playerConfigs={isUsedOnMobile ? playerMobileConfig : playerDesktopConfig}
							queryParams={queryParams}
							isInlinePlayer={isInlinePlayer}
							ref={countdownRef}
						/>
						{mediaType === 'scenario' && <TransitionLayer />}
					</>}
					{fromSceneEditor && renderSceneEditorPlayer()}
				</>
			)}
		</div>
	);
};

export default App;
